/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';
import { IDisposable, languages, editor, Position } from 'monaco-editor';
import { Monaco } from '@monaco-editor/react';
import { FieldReferenceMap } from './types';
import { configureMonacoYaml } from 'monaco-yaml';
import { buildSchema } from './schema';
import { CWTSchema, CWTSchemaPropertySummary, CWTSchemaField, CWTSchemaTable } from '../../../types/custom-worksheet-v2';

const menuID = 'custom';
const triggerCharacters = ['$'];

type SandboxConfig = {
    monaco: Monaco | null;
    availableReferences: FieldReferenceMap[];
    currentField: CWTSchemaField | CWTSchemaPropertySummary | CWTSchemaTable;
    schema: CWTSchema;
};
/**
 * Configures the Monaco editor for the sandbox. It provides calculation specific actions and auto completion
 *
 * @param monaco The Monaco editor instance.
 * @param availableReferences The available field references.
 */
export function useConfigureSandbox({ monaco, currentField, availableReferences, schema }: SandboxConfig): void {
    useEffect(() => {
        if (!monaco) {
            return;
        }

        const referenceStrings = availableReferences.map((r) => r.ref);

        const disposable = configureMonacoYaml(monaco, {
            validate: true,
            enableSchemaRequest: false,
            schemas: buildSchema(schema, currentField, referenceStrings),
        });

        // Add quick actions to the right click menu
        const actionDisposables = addActions(monaco.editor);

        // Add autocomplete of available references
        const autoCompleteDisposable = monaco.languages.registerCompletionItemProvider('yaml', {
            triggerCharacters,
            provideCompletionItems: (model, position) => addCompletionItems(model, position, availableReferences),
        });

        return (): void => {
            disposable.dispose();
            actionDisposables.forEach((d) => d.dispose());
            autoCompleteDisposable.dispose();
        };
    }, [monaco, availableReferences, schema, currentField]);
}

/**
 * Adds completion items for available references.
 *
 * @param model The text model.
 * @param position The cursor position.
 * @param availableReferences The available field references.
 * @returns The completion list.
 */
function addCompletionItems(
    model: editor.ITextModel,
    position: Position,
    availableReferences: FieldReferenceMap[]
): languages.CompletionList {
    const word = model.getWordUntilPosition(position);
    return {
        suggestions: availableReferences.map((r) => {
            return {
                label: r.ref,
                detail: r.field.name,
                insertText: r.ref,
                documentation: r.field.type.toLowerCase(),
                kind: languages.CompletionItemKind.Variable,
                range: {
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                    startColumn: word.startColumn,
                    endColumn: word.endColumn,
                },
            };
        }),
    };
}

function addActions(editor: any): IDisposable[] {
    return [
        addRefAction(editor),
        addFunctionAction(editor),
        addExpressionAction(editor),
        addConditionAction(editor),
        addGeneratorAction(editor),
    ];
}

function addFunctionAction(editor: any): IDisposable {
    return editor.addEditorAction({
        label: `Add Function`,
        id: 'func',
        contextMenuGroupId: menuID,
        run: function (_editor: any) {
            _editor.trigger('keyboard', 'paste', {
                text: FunctionSnippet,
            });
        },
    });
}

function addGeneratorAction(editor: any): IDisposable {
    return editor.addEditorAction({
        label: `Add Generator`,
        id: 'gen',
        contextMenuGroupId: menuID,
        run: function (_editor: any) {
            _editor.trigger('keyboard', 'paste', {
                text: GeneratorSnippet,
            });
        },
    });
}

function addRefAction(editor: any): IDisposable {
    return editor.addEditorAction({
        label: `Add Reference`,
        id: 'ref',
        contextMenuGroupId: menuID,
        run: function (_editor: any) {
            _editor.trigger('keyboard', 'type', {
                text: RefSnippet,
            });
        },
    });
}

function addExpressionAction(editor: any): IDisposable {
    return editor.addEditorAction({
        label: `Add Expression`,
        id: 'exp',
        contextMenuGroupId: menuID,
        run: function (_editor: any) {
            _editor.trigger('keyboard', 'paste', {
                text: ExpressionSnippet,
            });
        },
    });
}

function addConditionAction(editor: any): IDisposable {
    return editor.addEditorAction({
        label: `Add Condition`,
        id: 'cond',
        contextMenuGroupId: menuID,
        run: function (_editor: any) {
            _editor.trigger('keyboard', 'paste', {
                text: ConditionSnippet,
            });
        },
    });
}

export const GeneratorSnippet = `type: "GENERATOR"\nreturn:\n    operation: ""\n    params:\n      -\n      -
`;
export const RefSnippet = `$`;

export const FunctionSnippet = `type: "FUNCTION"\nreturn:\n    operation: ""\n    params:\n      -\n      -
`;

export const ExpressionSnippet = `type: "EXPRESSION"
return:
    operation: ""
    params:
        -
        -
`;

export const ConditionSnippet = `type: "CONDITION"
return:
    if:
        lhs: ""
        operation: ""
        rhs: ""
    then:
        return: true
    else:
        return: false
`;
